import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch } from "@/store";
import type { BirthdayNotificationTemplate } from "@/types/notifications";
import { useAuth } from "@/context/AuthContext";
import {
  fetchNotificationsBirthday,
  createNotificationsBirthday,
  updateNotificationsBirthday,
  deleteNotificationsBirthday,
} from "@/store/modules/notificationsBirthday/thunks";

const useNotificationsBirthday = (onSuccess?: () => void) => {
  const dispatch = useDispatch<AppDispatch>();
  const { establishment, baseURL } = useAuth();
  const { data, loading, error } = useSelector(
    (state: any) =>
      state.notificationsBirthday || {
        data: null as BirthdayNotificationTemplate[] | null,
        loading: false,
        error: null,
      },
  );

  const refresh = useCallback(
    (params?: any) => {
      if (!establishment || !baseURL) return;
      dispatch(fetchNotificationsBirthday(params || ({} as any)));
    },
    [dispatch, establishment, baseURL],
  );

  const createTemplate = useCallback(
    async (payload: Omit<BirthdayNotificationTemplate, "id">) => {
      const result = await dispatch(createNotificationsBirthday(payload));
      if (createNotificationsBirthday.fulfilled.match(result) && onSuccess)
        onSuccess();
      return result;
    },
    [dispatch, onSuccess],
  );

  const updateTemplate = useCallback(
    async (payload: Partial<BirthdayNotificationTemplate> & { id: number }) => {
      const result = await dispatch(updateNotificationsBirthday(payload));
      if (updateNotificationsBirthday.fulfilled.match(result) && onSuccess)
        onSuccess();
      return result;
    },
    [dispatch, onSuccess],
  );

  const removeTemplate = useCallback(
    async (id: number) => {
      const result = await dispatch(deleteNotificationsBirthday(id));
      if (deleteNotificationsBirthday.fulfilled.match(result) && onSuccess)
        onSuccess();
      return result;
    },
    [dispatch, onSuccess],
  );

  return {
    templates: data,
    loading,
    error,
    refresh,
    createTemplate,
    updateTemplate,
    removeTemplate,
  };
};

export default useNotificationsBirthday;
